import axios from 'axios';
import jwt from 'jsonwebtoken';
import { defineMessages } from 'react-intl';
import swal from 'sweetalert';
import UserProfile from '../Common/UserProfile';
import localeData from '../../server/locales/data.json';
import LogoffRun from '../Layout/Logoff.run';

const ACCESS_TOKEN_SKEW_SECONDS = 30;
const AUTH_SESSION_ENDED_KEY = 'AUTH_SESSION_ENDED';

let ensureSessionPromise = null;

var PORT = '';
if (window.location.hostname === 'localhost') {
  PORT = ':3000';
}
const API_URL = window.location.protocol + '//' + window.location.hostname + PORT + '/api/';

const messages = defineMessages({
  sessionSupersededTitle: {
    id: 'AuthSession.sessionSupersededTitle',
    defaultMessage: 'Session ended'
  },
  sessionSupersededText: {
    id: 'AuthSession.sessionSupersededText',
    defaultMessage: 'It looks like your account has been accessed somewhere else.'
  },
  sessionExpiredTitle: {
    id: 'AuthSession.sessionExpiredTitle',
    defaultMessage: 'Session expired'
  },
  sessionExpiredText: {
    id: 'AuthSession.sessionExpiredText',
    defaultMessage: 'Your session has expired. Please sign in again to continue.'
  },
  sessionEndedConfirm: {
    id: 'AuthSession.sessionEndedConfirm',
    defaultMessage: 'OK'
  }
});

/** Auth errors that should explain why we are sending the user back to login. */
const SESSION_ENDED_MODAL = {
  SESSION_SUPERSEDED: 'superseded',
  REFRESH_TOKEN_EXPIRED: 'expired',
  REFRESH_TOKEN_REUSE: 'expired',
  INVALID_REFRESH_TOKEN: 'expired',
  MISSING_REFRESH_TOKEN: 'expired',
  INVALID_REFRESH_RESPONSE: 'expired',
  TOKEN_EXPIRED: 'expired'
};

let isRefreshing = false;
let isLoggingOut = false;
let refreshPromise = null;

function getAuthErrorName(error) {
  return (
    (error &&
      error.response &&
      error.response.data &&
      error.response.data.error &&
      error.response.data.error.name) ||
    (error && error.name) ||
    null
  );
}

function resolveLocaleMessages() {
  const language =
    (navigator.languages && navigator.languages[0]) ||
    navigator.language ||
    navigator.userLanguage ||
    'pt-BR';
  const languageWithoutRegionCode = String(language).split(/[_-]+/)[0].toLowerCase();
  const fullLocale = String(language).toLowerCase();

  return (
    localeData[fullLocale] ||
    localeData[languageWithoutRegionCode] ||
    localeData['pt-br'] ||
    localeData.en ||
    {}
  );
}

function formatMessage(descriptor) {
  const catalog = resolveLocaleMessages();
  return catalog[descriptor.id] || descriptor.defaultMessage;
}

function goToLogin() {
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

function persistSessionEndedReason(modalKind) {
  try {
    sessionStorage.setItem(AUTH_SESSION_ENDED_KEY, modalKind);
  } catch (e) {
    // ignore quota / private mode
  }
}

/**
 * Show the session-ended dialog (login page). Returns a Promise that resolves when OK is clicked.
 */
export function showSessionEndedModal(modalKind) {
  const kind = modalKind === 'superseded' ? 'superseded' : 'expired';
  const title =
    kind === 'superseded'
      ? formatMessage(messages.sessionSupersededTitle)
      : formatMessage(messages.sessionExpiredTitle);
  const text =
    kind === 'superseded'
      ? formatMessage(messages.sessionSupersededText)
      : formatMessage(messages.sessionExpiredText);

  try {
    if (typeof swal.close === 'function') swal.close();
  } catch (e) {
    /* ignore */
  }

  return swal({
    icon: 'warning',
    title,
    text,
    button: formatMessage(messages.sessionEndedConfirm),
    closeOnClickOutside: false,
    closeOnEsc: false
  });
}

/**
 * Consumes a pending session-ended reason left by forceLogout (shown on /login).
 * @returns {'superseded'|'expired'|null}
 */
export function consumeSessionEndedReason() {
  try {
    const kind = sessionStorage.getItem(AUTH_SESSION_ENDED_KEY);
    if (kind) sessionStorage.removeItem(AUTH_SESSION_ENDED_KEY);
    return kind === 'superseded' || kind === 'expired' ? kind : null;
  } catch (e) {
    return null;
  }
}

export function isSessionEnding() {
  return isLoggingOut;
}

export function forceLogout(error) {
  if (isLoggingOut) return;
  isLoggingOut = true;

  try {
    LogoffRun();
  } catch (e) {
    console.error('Could not tear down Chatwoot on logout', e);
  }

  try {
    UserProfile.clear();
  } catch (e) {
    console.error('Could not clear the local session', e);
  }

  try {
    if (typeof swal.close === 'function') swal.close();
  } catch (e) {
    /* ignore */
  }

  const modalKind = SESSION_ENDED_MODAL[getAuthErrorName(error)];
  if (modalKind) {
    persistSessionEndedReason(modalKind);
  }

  goToLogin();
}

export async function refreshAccessToken() {
  const refreshToken = UserProfile.getRefreshToken();
  if (!refreshToken) {
    const err = new Error('Missing refresh token');
    err.name = 'MISSING_REFRESH_TOKEN';
    throw err;
  }

  const response = await axios.post(API_URL + 'AccountUsers/refresh', {
    refreshToken
  });

  const accessToken = response.data && response.data.accessToken;
  if (!accessToken) {
    const err = new Error('Refresh response missing accessToken');
    err.name = 'INVALID_REFRESH_RESPONSE';
    throw err;
  }

  const refreshExpiresIn = response.data && response.data.refreshExpiresIn;
  UserProfile.setToken(accessToken, refreshExpiresIn || 0);

  const nextRefreshToken = response.data && response.data.refreshToken;
  if (nextRefreshToken) {
    UserProfile.setRefreshToken(nextRefreshToken, refreshExpiresIn || 0);
  }

  return accessToken;
}

export function getSharedRefreshPromise() {
  if (isLoggingOut) {
    const err = new Error('Session ending');
    err.name = 'SESSION_ENDING';
    return Promise.reject(err);
  }

  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = refreshAccessToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

export function isAccessTokenExpiredOrNearExpiry(skewSeconds = ACCESS_TOKEN_SKEW_SECONDS) {
  const token = UserProfile.getToken();
  if (!token) return true;

  try {
    const decoded = jwt.decode(token);
    if (!decoded || typeof decoded.exp !== 'number') return true;

    return decoded.exp <= Math.floor(Date.now() / 1000) + skewSeconds;
  } catch (e) {
    return true;
  }
}

/**
 * SAML sessions renew via backend header `refresh_token` (IdP token inside the JWT). They may not have the opaque `userp_refresh` cookie — do not treat that as logout.
 */
export function isSamlAccessToken(token = UserProfile.getToken()) {
  if (!token) return false;

  try {
    const decoded = jwt.decode(token);

    return Boolean(decoded && decoded.samlID);
  } catch (e) {
    return false;
  }
}

export function ensureValidSession() {
  if (isLoggingOut) {
    const err = new Error('Session ending');
    err.name = 'SESSION_ENDING';

    return Promise.reject(err);
  }

  if (ensureSessionPromise) return ensureSessionPromise;

  ensureSessionPromise = (async () => {
    if (!UserProfile.getToken()) {
      forceLogout();
      const err = new Error('Missing access token');
      err.name = 'TOKEN_NOT_FOUND';
      throw err;
    }

    if (!isAccessTokenExpiredOrNearExpiry()) {
      return UserProfile.getToken();
    }

    // Legacy/SAML-only sessions: let the request hit the API so server.js can renew via the refresh_token response header.
    if (!UserProfile.getRefreshToken()) {
      if (isSamlAccessToken()) return UserProfile.getToken();

      const err = new Error('The provided JWT has expired');
      err.name = 'TOKEN_EXPIRED';
      forceLogout(err);
      throw err;
    }

    try {
      return await getSharedRefreshPromise();
    } catch (error) {
      forceLogout(error);
      throw error;
    }
  })().finally(() => {
    ensureSessionPromise = null;
  });

  return ensureSessionPromise;
}
